import type { AgentPointer } from '@blocksuite/store';

export type RemotePresenceCursorLabelPlacement =
  | {
      cursorHeight: number;
      placement: 'above';
    }
  | {
      placement: 'below';
    };

export const REMOTE_PRESENCE_COLORS = [
  'var(--streamlain-multi-players-purple)',
  'var(--streamlain-multi-players-magenta)',
  'var(--streamlain-multi-players-red)',
  'var(--streamlain-multi-players-orange)',
  'var(--streamlain-multi-players-green)',
  'var(--streamlain-multi-players-blue)',
  'var(--streamlain-multi-players-brown)',
  'var(--streamlain-multi-players-grey)',
] as const;

export const DEFAULT_REMOTE_PRESENCE_COLOR = REMOTE_PRESENCE_COLORS[0];
export const REMOTE_PRESENCE_CURSOR_RESPONSE_MS = 45;
export const REMOTE_PRESENCE_CURSOR_SNAP_THRESHOLD = 0.5;

export interface RemotePresenceCursorPosition {
  x: number;
  y: number;
}

export function stepRemotePresenceCursorPosition(args: {
  current: RemotePresenceCursorPosition;
  target: RemotePresenceCursorPosition;
  elapsedMs: number;
}): { next: RemotePresenceCursorPosition; moving: boolean } {
  const dx = args.target.x - args.current.x;
  const dy = args.target.y - args.current.y;
  if (
    Math.abs(dx) <= REMOTE_PRESENCE_CURSOR_SNAP_THRESHOLD &&
    Math.abs(dy) <= REMOTE_PRESENCE_CURSOR_SNAP_THRESHOLD
  ) {
    return {
      moving: false,
      next: {
        x: args.target.x,
        y: args.target.y,
      },
    };
  }

  const elapsed = Math.min(Math.max(args.elapsedMs, 0), 64);
  const lerpFactor =
    1 - Math.exp(-elapsed / REMOTE_PRESENCE_CURSOR_RESPONSE_MS);
  return {
    moving: true,
    next: {
      x: args.current.x + dx * lerpFactor,
      y: args.current.y + dy * lerpFactor,
    },
  };
}

export function getRemotePresenceColor(seed: string | number): string {
  const value = String(seed);
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return (
    REMOTE_PRESENCE_COLORS[hash % REMOTE_PRESENCE_COLORS.length] ??
    DEFAULT_REMOTE_PRESENCE_COLOR
  );
}

export function createRemotePresenceCursorLabelStyle(
  color: string,
  placement: RemotePresenceCursorLabelPlacement
): Record<string, string> {
  return {
    position: 'absolute',
    left: '-4px',
    ...(placement.placement === 'above'
      ? { bottom: `${placement.cursorHeight - 4}px` }
      : { top: 'calc(100% + 3px)' }),
    backgroundColor: color,
    color: 'white',
    maxWidth: '160px',
    padding: '0 5px',
    border: '1px solid var(--streamlain-v2-layer-blackAlpha-10)',
    boxShadow: '0px 1px 6px 0px rgba(0, 0, 0, 0.16)',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '18px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

// Writer id of an external guest (share link without an account); the cursor label marks it with a
// guest badge behind the name.
export const EXTERNAL_GUEST_PRESENCE_ID_PREFIX = 'external-doc-access:';

export function isExternalGuestPresenceUser(
  user: { id?: string } | null | undefined
): boolean {
  return user?.id?.startsWith(EXTERNAL_GUEST_PRESENCE_ID_PREFIX) ?? false;
}

export type RemotePresenceTranslator = (
  label: 'guest' | 'crewmate'
) => string | undefined;

let presenceTranslator: RemotePresenceTranslator | undefined;

export function registerRemotePresenceTranslator(
  translator: RemotePresenceTranslator
): void {
  presenceTranslator = translator;
}

export function remotePresenceGuestBadgeLabel(): string {
  return presenceTranslator?.('guest') ?? 'Guest';
}

export function remotePresenceCrewmateLabel(): string {
  return presenceTranslator?.('crewmate') ?? 'Mate';
}

export function agentPointerLabel(
  pointer: AgentPointer,
  ownerName: string | null | undefined
): string {
  return pointer.agent.kind === 'named'
    ? pointer.agent.name
    : ownerName?.trim() || remotePresenceCrewmateLabel();
}

// Guest label: name (ellipsis) and badge side by side, the badge never cut off; the badge a white
// 10 / 14 pill in the cursor colour.
export function createRemotePresenceGuestLabelStyles(color: string): {
  label: Record<string, string>;
  name: Record<string, string>;
  badge: Record<string, string>;
} {
  return {
    label: { display: 'flex', alignItems: 'center', gap: '4px' },
    name: {
      minWidth: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    badge: {
      flex: 'none',
      padding: '0 4px',
      borderRadius: '3px',
      backgroundColor: 'white',
      color,
      fontSize: '10px',
      fontWeight: '600',
      lineHeight: '14px',
    },
  };
}

function getCurrentOrigin(): string | undefined {
  return typeof location === 'undefined' ? undefined : location.origin;
}

export function isRenderableRemotePresenceAvatarUrl(
  url: string | null | undefined,
  baseOrigin = getCurrentOrigin()
): url is string {
  const value = url?.trim();
  if (!value) {
    return false;
  }

  if (value.startsWith('blob:')) {
    return true;
  }

  if (value.startsWith('data:image/')) {
    return true;
  }

  if (!baseOrigin) {
    return false;
  }

  try {
    const parsed = new URL(value, baseOrigin);
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      parsed.origin === baseOrigin
    );
  } catch {
    return false;
  }
}
